/**
 * trustLatticeService — SENTINEL-CORE-1 / F-SENTINEL-TRUST-001
 *
 * Maintains per-agent trust scores based on three signals:
 *   accuracySignal  — derived from finalDisposition (completed = 1.0, failed = 0.0, blocked = 0.5)
 *   dignitySignal   — derived from dignityVerdict.passed
 *   complianceSignal — derived from enforcerVerdict.returnType (stated_position = 1.0, else 0.5)
 *
 * Trust score is a rolling exponential moving average over a configurable
 * window. Scores live in Firestore per tenant + globally aggregated.
 *
 * Writes via DataService; emits SENTINEL-TRUST-UPDATED via EventBusService.
 * KTRUST-L1: fire-and-forget — trust update failure never blocks caller.
 */

import DataService from '../DataService';
import EventBusService from '../EventBusService';

const ROLLING_ALPHA = 0.1;
const DEFAULT_SCORE = 0.8;

function _accuracySignal(finalDisposition) {
  if (!finalDisposition || finalDisposition === 'completed') return 1.0;
  if (finalDisposition === 'failed') return 0.0;
  return 0.5;
}

function _dignitySignal(dignityVerdict) {
  if (!dignityVerdict) return 1.0;
  return dignityVerdict.passed ? 1.0 : 0.0;
}

function _complianceSignal(enforcerVerdict) {
  if (!enforcerVerdict) return 1.0;
  return enforcerVerdict.returnType === 'stated_position' ? 1.0 : 0.5;
}

function _computeActionScore(signals) {
  return (signals.accuracySignal + signals.dignitySignal + signals.complianceSignal) / 3;
}

function _ema(current, newSample, alpha) {
  return alpha * newSample + (1 - alpha) * current;
}

/**
 * Update trust lattice for an agent action.
 *
 * @param {object} opts
 * @param {string} opts.tenantId
 * @param {string} opts.agentId
 * @param {string} opts.module
 * @param {string} opts.decisionType
 * @param {object} opts.result  - governed call result (finalDisposition, dignityVerdict, enforcerVerdict)
 * @returns {Promise<{newScore: number, delta: number}>}
 */
export async function updateTrust(opts) {
  const { tenantId, agentId, module: mod, decisionType, result } = opts;
  const timestamp = new Date().toISOString();

  let existing = null;
  try {
    existing = await DataService.get(
      `sentinel_trust_lattice/${tenantId}/agents`,
      agentId
    );
  } catch {
    // First action for this agent — start fresh.
  }

  const previousScore = existing?.score ?? DEFAULT_SCORE;
  const windowSize = (existing?.windowSize ?? 0) + 1;

  const scoringSignals = {
    accuracySignal:   _accuracySignal(result?.finalDisposition),
    dignitySignal:    _dignitySignal(result?.dignityVerdict),
    complianceSignal: _complianceSignal(result?.enforcerVerdict),
  };

  const actionScore = _computeActionScore(scoringSignals);
  const newScore = _ema(previousScore, actionScore, ROLLING_ALPHA);
  const delta = newScore - previousScore;

  const latticeRecord = {
    tenantId,
    agentId,
    module: mod,
    score: newScore,
    windowSize,
    lastActionDecisionType: decisionType,
    lastUpdated: timestamp,
    softDeleted: null,
  };

  await DataService.set(
    `sentinel_trust_lattice/${tenantId}/agents`,
    agentId,
    latticeRecord
  );

  // Global aggregate (cross-tenant view for operator security ops).
  await DataService.set(
    'sentinel_trust_lattice_global/agents',
    agentId,
    {
      agentId,
      latestScore: newScore,
      latestUpdated: timestamp,
      sampleTenantId: tenantId,
      softDeleted: null,
    }
  );

  await EventBusService.publish({
    eventType: 'sentinel.trust.updated',
    tenantId,
    sourceModule: 'sentinel',
    sourceId: agentId,
    payload: {
      agentId,
      module: mod,
      decisionType,
      previousScore,
      newScore,
      delta,
      scoringSignals,
      windowSize,
      timestamp,
    },
    actor: { id: agentId, role: 'sentinel_substrate' },
  });

  return { newScore, delta };
}

export default { updateTrust };
